// @flow
import { APICore } from "./apiCore";
import i18n from "i18next";
import { VERSION } from "../../constants/version";
// AWS Amplify v6
import {
  signIn,
  signOut,
  signUp,
  confirmSignUp,
  confirmSignIn,
  resetPassword,
  confirmResetPassword,
  getCurrentUser,
  fetchUserAttributes,
  setUpTOTP,
  verifyTOTPSetup,
  updateMFAPreference,
  fetchMFAPreference,
} from "aws-amplify/auth";
import { client } from "../amplifyClient";
import * as customQueries from "./graphql/auth.queries";
import { callLambda } from "./gateway";
import { languages } from "../../constants/languages";

const api = new APICore();

const SUPPORTED_LANGUAGES = languages.map((l) => l.languageCode);

// Normalize the current UI language (e.g. "de-DE" -> "de"), clamp to a supported
// code, fallback to English. Used to tell Cognito which language to send emails in.
function getUiLanguage() {
  const raw = (i18n.language || "en").toLowerCase();
  const normalized = raw.includes("-") ? raw.split("-")[0] : raw;
  return SUPPORTED_LANGUAGES.includes(normalized) ? normalized : "en";
}
// Builds the app's logged-in user object from the CURRENT Cognito session.
// Shared by login() (single-step sign-in) and confirmMfa() (after a TOTP challenge),
// so both paths return an identical [200, user] tuple.
async function buildLoggedInUser() {
  const cognitoUser = await getCurrentUser();
  const attributes = await fetchUserAttributes();
  const userDetails = await client.graphql({
    query: customQueries.getLoggedInUser,
    variables: { id: cognitoUser.userId },
  });
  const ddUser = userDetails.data.getUsers;
  let contactPerson = null;
  try {
    const doc = await client.graphql({
      query: customQueries.customGetContactPerson,
      variables: { id: ddUser.contactPersonUserId },
    });
    contactPerson = doc.data.customGetContactPerson;
  } catch (_) {}
  return [
    200,
    {
      id: cognitoUser.userId,
      companyContactPersonUserId: ddUser.companyContactPersonUserId,
      username: attributes.email,
      country: ddUser.country,
      firstName: ddUser.firstName,
      lastName: ddUser.lastName,
      email: attributes.email,
      permissions: ddUser.role.permissions,
      role: ddUser.role.roleName,
      roleId: ddUser.roleId,
      language: ddUser.language,
      type: ddUser.role.type,
      invitableCompanyTypes: ddUser.role.invitableCompanyTypes || [],
      company: ddUser.company,
      companyId: ddUser.companyId,
      companyName: ddUser.company?.name,
      contactPerson: contactPerson,
      notifications: JSON.parse(ddUser.notifications),
      productTourStatus: JSON.parse(ddUser.productTourStatus),
      pushNotifications: ddUser.pushNotifications
        ? JSON.parse(ddUser.pushNotifications)
        : {},
      settings: ddUser.settings && JSON.parse(ddUser.settings),
      version: VERSION,
    },
  ];
}

// account
function login(params) {
  const doSignIn = async () => {
    const result = await signIn({
      username: params.username,
      password: params.password,
    });
    // Users enrolled in TOTP get a challenge instead of an immediate session.
    // Signal the caller to collect the 6-digit code — do NOT call getCurrentUser()
    // yet (there is no session until the challenge is answered). Users who are NOT
    // enrolled fall straight through to buildLoggedInUser(), exactly as before, so
    // this is backward-compatible whether or not MFA is enabled on the pool.
    if (
      result &&
      result.isSignedIn === false &&
      result.nextStep &&
      result.nextStep.signInStep === "CONFIRM_SIGN_IN_WITH_TOTP_CODE"
    ) {
      return [200, { mfaRequired: true }];
    }
    return await buildLoggedInUser();
  };

  return new Promise(function (resolve, reject) {
    doSignIn()
      .then(resolve)
      .catch(async (error) => {
        // v6 throws UserAlreadyAuthenticatedException when a stale Cognito session exists
        // (e.g. user manually cleared app state without a proper signOut). Sign out first, then retry.
        if (error.name === "UserAlreadyAuthenticatedException") {
          try {
            await signOut();
            const result = await doSignIn();
            resolve(result);
          } catch (retryError) {
            console.error("Error after signOut retry", retryError);
            reject([401, { statusCode: 401, message: "Error in authentication." }]);
          }
        } else {
          console.error("Error", error);
          reject([401, { statusCode: 401, message: "Error in authentication." }]);
        }
      });
  });
}

// Completes a TOTP MFA challenge started by login(): submits the 6-digit code and,
// on success, builds the same logged-in user object login() returns.
function confirmMfa(params) {
  return new Promise(function (resolve, reject) {
    confirmSignIn({ challengeResponse: params.code })
      .then(async (result) => {
        if (result && result.isSignedIn) {
          try {
            const user = await buildLoggedInUser();
            resolve(user);
          } catch (buildError) {
            console.error("Error building user after MFA", buildError);
            reject([
              401,
              { statusCode: 401, message: "Error in authentication." },
            ]);
          }
        } else {
          reject([
            401,
            { message: "The authentication code is invalid. Please try again." },
          ]);
        }
      })
      .catch((error) => {
        console.error("Error confirming MFA", error);
        const name = (error && error.name) || "";
        const text = (error && error.message) || "";
        if (name === "CodeMismatchException") {
          // plain wrong code — stay on the code step
          reject([
            401,
            { message: "The authentication code is invalid. Please try again." },
          ]);
        } else if (name === "ExpiredCodeException") {
          // Cognito TOTP codes are single-use: the code of this 30 s window was
          // already consumed (e.g. by the previous sign-in) — stay on the code step
          reject([
            401,
            {
              message:
                "That code was not correct or was already used. Please wait for a new code and try again.",
            },
          ]);
        } else {
          // Everything else means the challenge itself is gone: Cognito expires the
          // sign-in session after ~3 minutes (NotAuthorizedException "Invalid session
          // for the user, session is expired"), or there is no sign-in in progress
          // any more (Amplify SignInException after a page refresh). The code can't be
          // retried against a dead session, so send the user back to the password
          // step. `expired` is what the reducer keys on; 999 renders as a warning.
          console.warn("MFA challenge no longer valid:", name, text);
          reject([
            401,
            {
              statusCode: 999,
              expired: true,
              message: "Your session has expired. Please sign in again.",
            },
          ]);
        }
      });
  });
}

// --- TOTP enrollment helpers (used by the account-settings 2FA screen) ---

// Begins TOTP setup for the signed-in user. Returns the otpauth:// URI (to render
// as a QR code) and the shared secret (for manual entry into an authenticator app).
async function startTotpSetup() {
  const totpSetupDetails = await setUpTOTP();
  const attributes = await fetchUserAttributes();
  const setupUri = totpSetupDetails.getSetupUri(
    "Line Up Dashboard",
    attributes.email,
  );
  return { uri: setupUri.href, secret: totpSetupDetails.sharedSecret };
}

// Verifies the first code from the authenticator app and makes TOTP the user's
// preferred second factor. Throws if the code is wrong (caller shows the error).
async function confirmTotpSetup(code) {
  await verifyTOTPSetup({ code });
  await updateMFAPreference({ totp: "PREFERRED" });
}

// Turns TOTP off for the signed-in user — but only after the user proves they still
// hold the second factor by entering a current code. Cognito's VerifySoftwareToken
// accepts a fresh code for an already-enrolled user without touching the enrollment
// (verified on devms 2026-10-07); a wrong code throws EnableSoftwareTokenMFAException
// "Code mismatch", a code that was already used in this 30 s window throws
// "... already been used once". Both surface as `error.code === "InvalidTotpCode"`
// so the UI can tell them apart from a failure of the actual disable call.
async function disableTotp(code) {
  try {
    await verifyTOTPSetup({ code });
  } catch (e) {
    const err = new Error(e && e.message ? e.message : "Invalid code");
    err.code = "InvalidTotpCode";
    throw err;
  }
  await updateMFAPreference({ totp: "DISABLED" });
}

// Returns true if the signed-in user currently has TOTP enabled.
async function getTotpEnabled() {
  const pref = await fetchMFAPreference();
  return Boolean(pref && pref.enabled && pref.enabled.includes("TOTP"));
}

function logout() {
  return new Promise(function (resolve, reject) {
    signOut()
      .then((_) => {
        resolve([200, {}]);
      })
      .catch((error) => {
        console.log("Error: ", error);
        reject([401, { statusCode: 401, message: "Error in authentication." }]);
      });
  });
}

/**
 * Re-fetches the logged-in user's role permissions and settings (e.g. quick
 * filters) without logging them out. Runs on every mount (see App.js) so
 * changes made on another device — a permission change or a quick filter
 * added on mobile — show up on refresh instead of requiring re-login. Runs
 * the same query login uses, over the app's normal ID-token-authorized
 * client, and returns only the fields that can go stale so the caller can
 * merge them without clobbering other cached user fields.
 *
 * NOTE: we deliberately do NOT force a Cognito token refresh here. The client
 * authorizes with the ID token (which carries the custom:companyId /
 * custom:userRoleId claims the @auth rules need); forcing a refresh made that
 * token unavailable to the request and getUsers came back null. The existing
 * session token already carries the claims, exactly like every other query.
 */
async function refreshUserPermissions(userId) {
  const res = await client.graphql({
    query: customQueries.getLoggedInUser,
    variables: { id: userId },
  });
  const ddUser = res?.data?.getUsers;
  if (!ddUser || !ddUser.role) {
    // Bail without touching the cached session — keep the existing permissions
    // and let it retry on the next load rather than throwing an opaque TypeError.
    throw new Error("Could not refresh permissions: getUsers returned null.");
  }

  return {
    permissions: ddUser.role.permissions,
    role: ddUser.role.roleName,
    roleId: ddUser.roleId,
    type: ddUser.role.type,
    invitableCompanyTypes: ddUser.role.invitableCompanyTypes || [],
    settings: ddUser.settings && JSON.parse(ddUser.settings),
  };
}

function invitationSignup(params) {
  // The invited user's language comes from the invitation (carried via the
  // &lang= query param on the register link). Fall back to the UI language.
  const language = SUPPORTED_LANGUAGES.includes(params.language)
    ? params.language
    : getUiLanguage();
  const setUser = async (user) => {
    const newUser = {
      userId: user.userId,
      invitationId: params.invitationId,
      newUser: {
        email: params.email,
        firstName: params.firstName,
        lastName: params.lastName,
        language: language,
      },
    };
    // Public route (no Cognito session yet — this runs during signup).
    try {
      await callLambda("lineupdashboardUserRegistrationHandler", {
        method: "POST",
        body: newUser,
        auth: false,
      });
    } catch (error) {
      throw [
        error.status || 400,
        { message: error.data?.message || "An error has occurred. Please try again." },
      ];
    }
  };

  return new Promise(function (resolve, reject) {
    let alert_message = "Username or password is incorrect";

    signUp({
      username: params.email,
      password: params.password,
      options: { clientMetadata: { language } },
    })
      .then(async (user) => {
        try {
          await setUser(user);
          resolve([200, user]);
        } catch (lambdaError) {
          reject(lambdaError);
        }
      })
      .catch((error) => {
        console.error("Error", error);
        switch (error.name) {
          case "InvalidPasswordException":
            alert_message =
              "Your selected password does not match the requirements. Please choose a new password with a minimum length of 8 characters.";
            break;
          case "UsernameExistsException":
            alert_message = "There is already a user with this email address";
            break;
          default:
            alert_message = "An error has occurred. Please try again.";
            break;
        }
        reject([400, { message: alert_message }]);
      });
  });
}

function signup(params) {
  const setUser = async (user) => {
    // Create User Document with User Id
    const newUser = {
      userId: user.userId,
      companyName: params.companyName,
      newUser: {
        email: params.email,
        firstName: params.firstName,
        lastName: params.lastName,
        language: getUiLanguage(),
        type: params.type,
        country: params.country,
      },
    };
    try {
      // Public route (no Cognito session yet — this runs during signup).
      await callLambda("lineupdashboardUserRegistrationHandler", {
        method: "POST",
        body: newUser,
        auth: false,
      });
    } catch (error) {
      console.error("Error", error);
    }
  };

  return new Promise(function (resolve, reject) {
    signUp({
      username: params.email,
      password: params.password,
      options: { clientMetadata: { language: getUiLanguage() } },
    })
      .then((user) => {
        setUser(user);
        resolve([200, user]);
      })
      .catch((error) => {
        console.error("Error", error);
        let alert_message;
        switch (error.name) {
          case "InvalidPasswordException":
            alert_message =
              "Your selected password does not match the requirements. Please choose a new password with a minimum length of 8 characters.";
            break;
          case "UsernameExistsException":
            alert_message = "There is already a user with this email address";
            break;
          default:
            alert_message = "An error has occurred. Please try again.";
            break;
        }
        reject([400, { message: alert_message }]);
      });
  });
}

function forgotPassword(params) {
  return new Promise(function (resolve, reject) {
    resetPassword({
      username: params.username,
      options: { clientMetadata: { language: getUiLanguage() } },
    })
      .then((response) => {
        let responseJson = {
          message:
            "We've sent you a link to reset your password to your registered email.",
        };
        resolve([200, responseJson]);
      })
      .catch((error) => {
        console.error("Error", error);
        reject([
          401,
          {
            message:
              "Sorry, we could not find any registered user with entered E-Mail.",
          },
        ]);
      });
  });
}

function newPassword(params) {
  return new Promise(function (resolve, reject) {
    confirmResetPassword({
      username: params.email,
      confirmationCode: params.code,
      newPassword: params.newPassword,
    })
      .then((response) => {
        resolve([
          200,
          {
            message:
              "Successfully updated your password. You can now login again.",
          },
        ]);
      })
      .catch((error) => {
        console.error("Error", error);
        reject([
          401,
          {
            message:
              "An error accured while updating your password. Please try again.",
          },
        ]);
      });
  });
}

function verifyEmail(params) {
  return new Promise(function (resolve, reject) {
    confirmSignUp({ username: params.email, confirmationCode: params.code })
      .then((response) => {
        resolve([
          200,
          {
            message: "Successfully verified your E-Mail. You can now login.",
          },
        ]);
      })
      .catch((error) => {
        console.log("Error: ", error);
        reject([
          401,
          {
            message: "The link you have used has expired.",
          },
        ]);
      });
  });
}

export {
  login,
  logout,
  confirmMfa,
  startTotpSetup,
  confirmTotpSetup,
  disableTotp,
  getTotpEnabled,
  refreshUserPermissions,
  signup,
  invitationSignup,
  forgotPassword,
  newPassword,
  verifyEmail,
};
